import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Building2, 
  ChevronRight,
  ChevronLeft,
  Check,
  User,
  Mail,
  Phone,
  Globe,
  DollarSign,
  ShoppingCart,
  MessageSquare,
  CreditCard,
  Upload,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Onboarding steps
const STEPS = [
  { id: 'company', title: 'Company Details', icon: Building2 },
  { id: 'admin', title: 'Admin User', icon: User },
  { id: 'integrations', title: 'Integrations', icon: ShoppingCart },
  { id: 'complete', title: 'Complete', icon: Check }
];

const CompanyOnboardingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createdCompanyId, setCreatedCompanyId] = useState(null);
  
  // Company details
  const [companyData, setCompanyData] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'UAE',
    currency: 'AED',
    timezone: 'Asia/Dubai',
    industry: ''
  });
  
  // Admin user details
  const [adminData, setAdminData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirm_password: ''
  });
  
  // Integration selections
  const [integrations, setIntegrations] = useState({
    shopify: false,
    whatsapp: false,
    mamopay: false,
    zoho: false
  });

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const validateCompanyStep = () => {
    if (!companyData.name.trim()) {
      toast.error('Company name is required');
      return false;
    }
    if (!companyData.email.trim()) {
      toast.error('Company email is required');
      return false;
    }
    return true;
  };

  const validateAdminStep = () => {
    if (!adminData.email.trim()) {
      toast.error('Admin email is required');
      return false;
    }
    if (!adminData.full_name.trim()) {
      toast.error('Admin name is required');
      return false;
    }
    if (!adminData.password || adminData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (adminData.password !== adminData.confirm_password) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const createCompany = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create company
      const companyRes = await axios.post(`${API_BASE_URL}/companies`, companyData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const companyId = companyRes.data.id;
      setCreatedCompanyId(companyId);
      
      // Create admin user for the company
      await axios.post(
        `${API_BASE_URL}/super-admin/companies/${companyId}/users`,
        {
          ...adminData,
          roles: ['admin'],
          is_admin: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Company and admin user created successfully!');
      setCurrentStep(2); // Move to integrations step
    } catch (error) {
      console.error('Failed to create company:', error);
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      if (validateCompanyStep()) {
        setCurrentStep(1);
      }
    } else if (currentStep === 1) {
      if (validateAdminStep()) {
        await createCompany();
      }
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Company Name *</Label>
              <Input
                value={companyData.name}
                onChange={(e) => setCompanyData({ ...companyData, name: e.target.value })}
                placeholder="Enter company name"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Email *</Label>
                <Input
                  type="email"
                  value={companyData.email}
                  onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                  placeholder="contact@company.com"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Phone</Label>
                <Input
                  value={companyData.phone}
                  onChange={(e) => setCompanyData({ ...companyData, phone: e.target.value })}
                  placeholder="+971 XX XXX XXXX"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Country</Label>
                <Select 
                  value={companyData.country} 
                  onValueChange={(value) => setCompanyData({ ...companyData, country: value })}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="UAE">UAE</SelectItem>
                    <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="USA">USA</SelectItem>
                    <SelectItem value="UK">UK</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Currency</Label>
                <Select 
                  value={companyData.currency} 
                  onValueChange={(value) => setCompanyData({ ...companyData, currency: value })}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="SAR">SAR</SelectItem>
                    <SelectItem value="INR">INR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Industry</Label>
              <Input
                value={companyData.industry}
                onChange={(e) => setCompanyData({ ...companyData, industry: e.target.value })}
                placeholder="e.g., Furniture, Electronics, Fashion"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-wt-text-secondary text-sm mb-4">
              Create an admin user who will manage this company.
            </p>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Full Name *</Label>
              <Input
                value={adminData.full_name}
                onChange={(e) => setAdminData({ ...adminData, full_name: e.target.value })}
                placeholder="John Doe"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Email *</Label>
              <Input
                type="email"
                value={adminData.email}
                onChange={(e) => setAdminData({ ...adminData, email: e.target.value })}
                placeholder="admin@company.com"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Password *</Label>
                <Input
                  type="password"
                  value={adminData.password}
                  onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                  placeholder="Minimum 8 characters"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Confirm Password *</Label>
                <Input
                  type="password"
                  value={adminData.confirm_password}
                  onChange={(e) => setAdminData({ ...adminData, confirm_password: e.target.value })}
                  placeholder="Confirm password"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-4">
            <p className="text-wt-text-secondary text-sm mb-4">
              Select which integrations you want to enable for this company.
              You can configure credentials later in company settings.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'shopify', name: 'Shopify', icon: ShoppingCart, color: 'green', desc: 'E-commerce integration' },
                { key: 'whatsapp', name: 'WhatsApp', icon: MessageSquare, color: 'emerald', desc: 'Business messaging' },
                { key: 'mamopay', name: 'MamoPay', icon: CreditCard, color: 'purple', desc: 'Payment gateway' },
                { key: 'zoho', name: 'Zoho Books', icon: DollarSign, color: 'blue', desc: 'Accounting software' }
              ].map((integration) => (
                <div
                  key={integration.key}
                  onClick={() => setIntegrations({ ...integrations, [integration.key]: !integrations[integration.key] })}
                  className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${integrations[integration.key] 
                      ? 'border-wt-gold bg-wt-gold/10' 
                      : 'border-wt-border bg-wt-bg hover:border-wt-gold/50'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${integration.color}-500/10`}>
                      <integration.icon className={`h-5 w-5 text-${integration.color}-400`} />
                    </div>
                    <div>
                      <p className="font-medium text-wt-text-primary">{integration.name}</p>
                      <p className="text-xs text-wt-text-secondary">{integration.desc}</p>
                    </div>
                  </div>
                  {integrations[integration.key] && (
                    <div className="mt-2 flex justify-end">
                      <Check className="h-5 w-5 text-wt-gold" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-wt-text-muted mt-4">
              Tip: You can always enable or disable integrations later from the company settings.
            </p>
          </div>
        );
      
      case 3:
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-wt-text-primary mb-2">
              Company Created Successfully!
            </h3>
            <p className="text-wt-text-secondary mb-6">
              {companyData.name} has been set up and is ready to use.
            </p>
            
            <div className="bg-wt-bg p-4 rounded-lg text-left mb-6">
              <h4 className="font-medium text-wt-text-primary mb-2">Admin Credentials</h4>
              <p className="text-sm text-wt-text-secondary">
                Email: <span className="text-wt-text-primary">{adminData.email}</span>
              </p>
              <p className="text-sm text-wt-text-secondary">
                Password: <span className="text-wt-text-primary">{adminData.password}</span>
              </p>
            </div>
            
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate('/super-admin/companies')}
                className="border-wt-border text-wt-text-primary"
              >
                View All Companies
              </Button>
              <Button
                onClick={() => navigate(`/super-admin/companies/${createdCompanyId}/settings`)}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              >
                Configure Integrations
              </Button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="company-onboarding">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Sparkles className="h-6 w-6 text-wt-gold" />
          <h1 className="text-2xl font-bold text-wt-text-primary">New Company Setup</h1>
        </div>
        <p className="text-wt-text-secondary">Let's get your new company up and running</p>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <Progress value={progress} className="h-2 bg-wt-border" />
        <div className="flex justify-between mt-4">
          {STEPS.map((step, index) => (
            <div 
              key={step.id}
              className={`flex flex-col items-center ${
                index <= currentStep ? 'text-wt-gold' : 'text-wt-text-muted'
              }`}
            >
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center mb-1
                ${index < currentStep ? 'bg-wt-gold text-black' : 
                  index === currentStep ? 'border-2 border-wt-gold' : 
                  'border border-wt-border'}
              `}>
                {index < currentStep ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <step.icon className="h-4 w-4" />
                )}
              </div>
              <span className="text-xs hidden sm:block">{step.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-wt-text-primary flex items-center gap-2">
            {React.createElement(STEPS[currentStep].icon, { className: "h-5 w-5 text-wt-gold" })}
            {STEPS[currentStep].title}
          </CardTitle>
          <CardDescription className="text-wt-text-secondary">
            Step {currentStep + 1} of {STEPS.length}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderStep()}
        </CardContent>
      </Card>

      {/* Navigation */}
      {currentStep < 3 && (
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="border-wt-border text-wt-text-primary"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={loading}
            className="bg-wt-gold hover:bg-wt-gold-dark text-black"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            {currentStep === 1 ? 'Create Company' : currentStep === 2 ? 'Complete Setup' : 'Next'}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyOnboardingPage;
